import { useState, useEffect } from 'react';

interface BlogPostSummary {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  coverImage: string | null;
  authorName: string;
  tags: string[];
  publishedAt: string;
}

interface BlogListPageProps {
  onNavigate: (page: string, data?: any) => void;
}

export default function BlogListPage({ onNavigate }: BlogListPageProps) {
  const [posts, setPosts] = useState<BlogPostSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/blog')
      .then(res => res.json())
      .then(data => {
        setPosts(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500"></div>
          <p className="text-gray-500 text-sm">Loading articles...</p>
        </div>
      </div>
    );
  }

  const featuredPost = posts[0];
  const remainingPosts = posts.slice(1);

  return (
    <div className="min-h-screen bg-gray-950">

      {/* Header */}
      <div className="bg-gradient-to-b from-gray-900 to-gray-950 border-b border-gray-800/50">
        <div className="max-w-5xl mx-auto px-4 py-14 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-400 text-xs font-semibold uppercase tracking-widest mb-5">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
            </svg>
            ScribeHaven Blog
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
            Resources for Writers
          </h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto leading-relaxed">
            Platform guides, writing tips, and honest comparisons for romance and romantasy authors who want to get discovered.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12">

        {posts.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <p className="text-gray-500 text-lg">No articles yet. Check back soon!</p>
          </div>
        ) : (
          <>
            {/* Featured post — large card */}
            {featuredPost && (
              <div className="mb-12">
                <p className="text-xs text-amber-400 font-semibold uppercase tracking-widest mb-4">Featured Article</p>
                <article
                  className="group cursor-pointer rounded-2xl overflow-hidden border border-gray-800 hover:border-amber-500/40 transition-all duration-300 bg-gray-900 hover:shadow-xl hover:shadow-amber-500/5"
                  onClick={() => onNavigate('blog-post', { slug: featuredPost.slug })}
                >
                  <div className="flex flex-col lg:flex-row">
                    {/* Cover image */}
                    <div className="lg:w-2/5 h-56 lg:h-auto bg-gradient-to-br from-amber-500/10 to-orange-600/10 flex-shrink-0 relative overflow-hidden">
                      {featuredPost.coverImage ? (
                        <img
                          src={featuredPost.coverImage}
                          alt={featuredPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-16 h-16 text-amber-500/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-8 flex flex-col justify-between flex-1">
                      <div>
                        {featuredPost.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {featuredPost.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="px-2.5 py-1 text-xs font-semibold bg-amber-500/10 text-amber-400 rounded-full border border-amber-500/20"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-amber-400 transition-colors leading-tight" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                          {featuredPost.title}
                        </h2>
                        <p className="text-gray-400 leading-relaxed text-base line-clamp-3" style={{ fontFamily: "'Georgia', serif" }}>
                          {featuredPost.excerpt}
                        </p>
                      </div>
                      <div className="flex items-center justify-between mt-6 pt-5 border-t border-gray-800">
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <div className="w-7 h-7 rounded-full bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                            <span className="text-amber-400 text-xs font-bold">
                              {featuredPost.authorName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span>{featuredPost.authorName}</span>
                          <span className="text-gray-700">·</span>
                          <time dateTime={featuredPost.publishedAt}>{formatDate(featuredPost.publishedAt)}</time>
                        </div>
                        <span className="text-amber-400 text-sm font-medium group-hover:gap-3 flex items-center gap-1.5 transition-all">
                          Read article
                          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            )}

            {/* Remaining posts grid */}
            {remainingPosts.length > 0 && (
              <>
                <p className="text-xs text-gray-600 font-semibold uppercase tracking-widest mb-6">More Articles</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {remainingPosts.map((post) => (
                    <article
                      key={post.id}
                      className="group cursor-pointer rounded-xl overflow-hidden border border-gray-800 hover:border-amber-500/40 transition-all duration-300 bg-gray-900 hover:shadow-lg hover:shadow-amber-500/5 flex flex-col"
                      onClick={() => onNavigate('blog-post', { slug: post.slug })}
                    >
                      {/* Cover image */}
                      <div className="h-44 bg-gradient-to-br from-gray-800 to-gray-900 relative overflow-hidden flex-shrink-0">
                        {post.coverImage ? (
                          <img
                            src={post.coverImage}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-10 h-10 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          </div>
                        )}
                        {/* Gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
                      </div>

                      {/* Content */}
                      <div className="p-6 flex flex-col flex-1">
                        {post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {post.tags.slice(0, 2).map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 text-xs font-medium bg-gray-800 text-amber-400 rounded-full border border-gray-700"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        <h2 className="text-lg font-bold text-white mb-3 group-hover:text-amber-400 transition-colors leading-snug flex-1" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                          {post.title}
                        </h2>
                        <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-4" style={{ fontFamily: "'Georgia', serif" }}>
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between pt-4 border-t border-gray-800 mt-auto">
                          <time dateTime={post.publishedAt} className="text-xs text-gray-600">
                            {formatDate(post.publishedAt)}
                          </time>
                          <span className="text-amber-400 text-xs font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                            Read
                            <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}

            {/* Bottom CTA */}
            <div className="mt-16 rounded-2xl border border-amber-500/20 p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.06) 0%, rgba(234,88,12,0.04) 100%)' }}>
              <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
                Ready to get your story discovered?
              </h3>
              <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
                Join ScribeHaven free. No paywalls, no contracts. Upload your first chapter today.
              </p>
              <button
                onClick={() => onNavigate('register')}
                className="inline-flex items-center gap-2 px-7 py-3 bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-amber-500/20"
              >
                Start Writing Free
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
